import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, ArrowDown, MapPin, Check, X as XIcon,
  ClipboardCheck, Warehouse, Factory, ShieldCheck, RefreshCw,
  Wrench, Ruler, MessageSquareWarning, FileSearch, GraduationCap,
  Rocket, BarChart3, GitBranch, Cpu, Zap,
  UserPlus, AlertTriangle, Truck, MessageCircleWarning, ClipboardList,
  FileText, Award, LayoutDashboard, Star, CheckCircle,
  Package, ScanLine
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════
   COLORS
   ═══════════════════════════════════════════════════════ */
const RED = '#E02D45';
const RED_HOVER = '#C82539';
const BLUE = '#38BDF8';

/* ═══════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════ */

const factoryApps = [
  { name: 'Incoming Quality', slug: 'incoming-quality', desc: 'Manage incoming inspections, supplier approvals, and sampling standards.', icon: ClipboardCheck },
  { name: 'Digital Stores', slug: 'digital-stores', desc: 'Track inventory, FIFO, bin locations, and material lots in real time.', icon: Warehouse },
  { name: 'Production Execution', slug: 'production-execution', desc: 'Monitor output, rejections, and operator-wise production across shifts.', icon: Factory },
  { name: 'Quality Management', slug: 'quality-management', desc: 'Run process checks, control plans, SPC, and inspection workflows digitally.', icon: ShieldCheck },
  { name: 'Change Management', slug: 'change-management', desc: 'Control 4M changes, capture abnormalities, and track corrective actions.', icon: RefreshCw },
  { name: 'Maintenance', slug: 'maintenance', desc: 'Manage machine history, PM schedules, breakdowns, and asset condition.', icon: Wrench },
  { name: 'Calibration Lab', slug: 'calibration-lab', desc: 'Track instruments, calibration due dates, certificates, and MSA records.', icon: Ruler },
  { name: 'Complaints & CAPA', slug: 'complaints-capa', desc: 'Manage complaints, quality alerts, 8D actions, and closure tracking.', icon: MessageSquareWarning },
  { name: 'Audit Management', slug: 'audit-management', desc: 'Digitize internal, process, product, dock, and layered audits.', icon: FileSearch },
  { name: 'Training / DOJO', slug: 'training-dojo', desc: 'Run skill tracking, training plans, assessments, and OJT records.', icon: GraduationCap },
  { name: 'NPD / APQP', slug: 'npd-apqp', desc: 'Track APQP milestones, PPAP status, drawings, and project progress.', icon: Rocket },
  { name: 'Management Dashboards', slug: 'management-dashboards', desc: 'View KPI trends, rejection analysis, department health, and reports.', icon: BarChart3 },
  { name: 'End-to-End Traceability', slug: 'traceability', desc: 'Connect material, process, inspection, and dispatch in one traceability chain.', icon: GitBranch },
  { name: 'IoT & Machine Data', slug: 'iot-machine-data', desc: 'Capture machine signals, OEE, runtime, alarms, and live line data.', icon: Cpu },
  { name: 'Energy Monitoring', slug: 'energy-monitoring', desc: 'Track energy use, machine-wise consumption, and cost visibility.', icon: Zap },
];

const vendorApps = [
  { name: 'Supplier Onboarding', slug: 'supplier-onboarding', desc: 'Digitize supplier registration, qualification, and approval workflows.', icon: UserPlus },
  { name: 'Line Incidents', slug: 'line-incidents', desc: 'Capture supplier-linked production issues and resolve them faster.', icon: AlertTriangle },
  { name: 'DOL Management', slug: 'dol-management', desc: 'Track delivery commitments, deviations, and follow-up actions.', icon: Truck },
  { name: 'Supplier Complaints', slug: 'supplier-complaints', desc: 'Manage supplier issues, responses, and closure records.', icon: MessageCircleWarning },
  { name: 'Supplier Audits', slug: 'supplier-audits', desc: 'Plan, conduct, score, and review supplier audits digitally.', icon: ClipboardList },
  { name: 'RFQ to LOI', slug: 'rfq-to-loi', desc: 'Track sourcing progress from quotation to commercial finalization.', icon: FileText },
  { name: 'Certificate Management', slug: 'certificate-management', desc: 'Store certificates, track expiry dates, and maintain compliance records.', icon: Award },
  { name: 'Supplier Dashboards', slug: 'supplier-dashboards', desc: 'Monitor supplier status, pending actions, and performance visibility.', icon: LayoutDashboard },
  { name: 'Ratings & Scorecards', slug: 'ratings-scorecards', desc: 'Review PPM, audit scores, and supplier performance in one place.', icon: Star },
  { name: 'Corrective Actions', slug: 'corrective-actions', desc: 'Track supplier CAPA actions through closure.', icon: CheckCircle },
];

const iotOnlyItems = ['Machine data', 'IoT dashboards', 'OEE tracking'];
const factoryOsItems = [
  'Quality management', 'Production execution', 'End-to-End Traceability',
  'Audit management', 'Stores & inventory', 'Maintenance & calibration',
  'Training & DOJO', 'CAPA & complaints', 'Supplier governance',
  'Management dashboards', 'NPD / APQP', 'And IoT & machine data',
];

const erpItems = ['Accounting', 'Material inwarding', 'Outwarding', 'Transactions', 'Business records'];
const mushinOpsItems = [
  'Quality & incoming inspection', 'Production execution', 'End-to-end traceability',
  'Audit management', 'Digital stores', 'Maintenance & calibration',
  'Training / DOJO', 'CAPA & complaints', 'Change management',
  'Management dashboards', 'IoT & machine data', 'Energy monitoring',
];

const industries = [
  { name: 'Automotive', image: 'https://images.unsplash.com/photo-1567789884554-0b844b597180?w=1200&q=80&fit=crop', standards: ['IATF 16949', 'VDA 6.3', 'Maruti VSA'], fit: 'Full plant digitisation. Quality systems, process audits, CAPA, layered approvals, and shopfloor discipline.' },
  { name: 'Aerospace', image: 'https://images.unsplash.com/photo-1764185800646-f75f7e16e465?w=1200&q=80&fit=crop', standards: ['AS9100'], fit: 'Documentation control, traceability, deviation handling, approvals, and audit readiness.' },
  { name: 'Pharmaceuticals', image: 'https://images.unsplash.com/photo-1748002388689-c62b45d5c28b?w=1200&q=80&fit=crop', standards: ['GMP', 'Schedule M'], fit: 'Batch traceability, SOP compliance, digital records, CAPA, and inspection readiness.' },
  { name: 'FMCG', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80&fit=crop', standards: ['Quality Systems', 'Production Control'], fit: 'Digital inspections, line visibility, quality checkpoints, and standardised workflows.' },
  { name: 'Export Manufacturing', image: 'https://images.unsplash.com/photo-1742535037733-b957136a6138?w=1200&q=80&fit=crop', standards: ['Buyer Compliance', 'Export QA'], fit: 'Quality documentation, traceability, corrective action, and audit readiness for global buyers.' },
];

const mapCities = [
  { name: 'Jalandhar', top: '21.3%', left: '25.4%' },
  { name: 'Ludhiana', top: '22.8%', left: '26.3%' },
  { name: 'Baddi', top: '21%', left: '29%' },
  { name: 'Parwanoo', top: '24.5%', left: '30%' },
  { name: 'Rudrapur', top: '29.1%', left: '37.5%' },
  { name: 'Manesar', top: '32.5%', left: '28.5%' },
  { name: 'Faridabad', top: '29.5%', left: '32%' },
  { name: 'Sanand', top: '48.8%', left: '15.3%' },
  { name: 'Dewas', top: '48.9%', left: '26.9%' },
  { name: 'Chakan', top: '62.7%', left: '20%' },
  { name: 'Bomasandra', top: '80.5%', left: '30%' },
  { name: 'Hosur', top: '84%', left: '34%' },
];

const traceSteps = [
  { label: 'Raw Material', sub: 'Inward & tagging', icon: Package },
  { label: 'Incoming Inspection', sub: 'Standards & sampling', icon: ClipboardCheck },
  { label: 'Digital Stores', sub: 'FIFO & bin placement', icon: Warehouse },
  { label: 'Production', sub: 'Line execution', icon: Factory },
  { label: 'In-Process Check', sub: 'Quality checkpoints', icon: ScanLine },
  { label: 'Final Inspection', sub: 'Sign-off & release', icon: ShieldCheck },
  { label: 'Dispatch', sub: 'Shipment & records', icon: Truck },
];

const clientsRow1 = [
  { name: 'Maruti Suzuki', logo: '/logos/maruti-suzuki.png' },
  { name: 'Tata Motors', logo: '/logos/tata-motors.png' },
  { name: 'Mahindra', logo: '/logos/mahindra.png' },
  { name: 'JCB India', logo: '/logos/jcb.jpg' },
  { name: 'JBM Group', logo: '/logos/jbm.png' },
  { name: 'Volvo Buses', logo: '/logos/volvo.png' },
  { name: 'DAF Trucks', logo: '/logos/daf.png' },
  { name: 'Navistar', logo: '/logos/navistar.png' },
  { name: 'Gabriel India', logo: '/logos/gabriel-india.png' },
  { name: 'Sandhar Technologies', logo: '/logos/sandhar.png' },
  { name: 'Anand', logo: '/logos/anand.png' },
  { name: 'RMX Industries', logo: '/logos/rmx.png' },
  { name: 'Top Ramen', logo: '/logos/top-ramen.png' },
  { name: 'IFB', logo: '/logos/IFB.png' },
  { name: 'Mankind', logo: '/logos/mankind.jpg' },
];

const clientsRow2 = [
  { name: 'Krishna', logo: '/logos/krishna.png' },
  { name: 'ALP Nishikawa', logo: '/logos/alp-nishikawa.png' },
  { name: 'Anand NVH', logo: '/logos/anand-nvh.png' },
  { name: 'SKH Metals', logo: '/logos/skh-metals.png' },
  { name: 'Bony Polymers', logo: '/logos/bony-polymers.png' },
  { name: 'Citizen', logo: '/logos/citizen.png' },
  { name: 'Imperial Auto', logo: '/logos/imperial-auto.png' },
  { name: 'Imperial Martor', logo: '/logos/imperial-martor.png' },
  { name: 'Ideal Precision', logo: '/logos/ideal-precision.png' },
  { name: 'Natraj Exports', logo: '/logos/natraj-exports.webp' },
  { name: 'Roop Automotives', logo: '/logos/roop-auto.png' },
  { name: 'Jayanita', logo: '/logos/jayanita.jpg' },
  { name: 'Panchal', logo: '/logos/panchal.jpg' },
  { name: 'Premier Seals', logo: '/logos/premierseals.jpg' },
  { name: 'Shresty', logo: '/logos/shresty.jpg' },
];

/* ═══════════════════════════════════════════════════════
   COMPONENTS
   ═══════════════════════════════════════════════════════ */

function Section({ children, className = '', id, ...props }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`} {...props}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={`${char}-${i}`}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{
              transitionDelay: `${startDelay + i * 70}ms`,
              color: highlightColor || undefined,
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

function AppCard({ name, desc, icon: Icon, variant = 'factory', slug }) {
  const isFactory = variant === 'factory';
  const accentColor = isFactory ? RED : BLUE;
  const linkTarget = isFactory ? `/products/factory-os#${slug}` : `/products/vendor-connect#${slug}`;
  return (
    <Link
      to={linkTarget}
      className={`group bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 block ${isFactory ? 'hover:bg-white/[0.06] hover:border-[#E8384F]/25' : 'hover:bg-white/[0.06] hover:border-[#38BDF8]/25'}`}
      data-testid={`app-${name.toLowerCase().replace(/[\s\/&]+/g, '-')}`}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-4 border"
        style={{
          backgroundColor: `${accentColor}12`,
          borderColor: `${accentColor}25`,
        }}
      >
        <Icon className="w-[18px] h-[18px]" style={{ color: accentColor }} strokeWidth={1.7} />
      </div>
      <h4 className="text-[13px] font-bold text-white/85 leading-snug">{name}</h4>
      <p className="mt-1.5 text-[12px] text-white/35 leading-relaxed">{desc}</p>
    </Link>
  );
}

function IndiaMapReal() {
  return (
    <div className="relative w-full max-w-[500px] mx-auto">
      <img src="/india-map.svg" alt="India Map" className="w-full h-auto opacity-60" />
      {mapCities.map((city) => (
        <div key={city.name} className="absolute group" style={{ top: city.top, left: city.left, transform: 'translate(-50%, -50%)' }}>
          <div className="relative flex items-center justify-center z-10">
            <div className="absolute w-5 h-5 rounded-full animate-ping opacity-20" style={{ backgroundColor: RED }} />
            <div className="w-2.5 h-2.5 rounded-full border border-white/20" style={{ backgroundColor: RED }} />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white/70 bg-black/60">
              {city.name}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════ */

export default function HomePage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [activeIndustry, setActiveIndustry] = useState(0);
  const [activeTrace, setActiveTrace] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  const rotateIndustry = useCallback(() => {
    setActiveIndustry(prev => (prev + 1) % industries.length);
  }, [setActiveIndustry]);

  useEffect(() => {
    const interval = setInterval(rotateIndustry, 5000);
    return () => clearInterval(interval);
  }, [rotateIndustry]);

  useEffect(() => {
    const t = setInterval(() => {
      setActiveTrace(prev => (prev + 1) % traceSteps.length);
    }, 1200);
    return () => clearInterval(t);
  }, [setActiveTrace]);

  return (
    <div>
      {/* ═══ 1. HERO ═══ */}
      <section className="relative min-h-screen flex items-end overflow-hidden -mt-16 bg-[#060606]" data-testid="hero-section">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-[0.38]" data-testid="hero-video">
          <source src="https://assets.mixkit.co/videos/47755/47755-720.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 w-full pb-24 lg:pb-32 pt-44">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <span className="inline-block text-sm sm:text-base font-bold uppercase tracking-[0.25em] text-white px-4 py-2 mb-10" style={{ backgroundColor: RED }} data-testid="hero-eyebrow">
              The Operating System for Factory Operations
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-[4.25rem] xl:text-[5.25rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.9]" data-testid="hero-heading">
            <TypewriterLine text="India's #1" visible={heroVisible} startDelay={500} />
            <TypewriterLine text="MES Platform" visible={heroVisible} startDelay={1300} />
            <TypewriterLine text="for Manufacturing 4.0" visible={heroVisible} startDelay={2100} highlightColor={RED} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '3800ms' }}>
            <div className="mt-12 h-[3px] w-16 hero-accent" style={{ backgroundColor: RED, animationDelay: '3900ms' }} />
            <p className="mt-8 text-lg sm:text-xl text-white/60 max-w-2xl leading-[1.7] font-medium" data-testid="hero-subtitle">
              Quality, execution, traceability, and machine intelligence. One connected operating layer for the modern factory.
            </p>
          </div>

          <div className={`mt-12 flex flex-wrap gap-5 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4200ms' }}>
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} onMouseEnter={e => e.currentTarget.style.backgroundColor = RED_HOVER} onMouseLeave={e => e.currentTarget.style.backgroundColor = RED} data-testid="hero-book-demo">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/20 hover:border-white/40 text-white/60 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300" data-testid="hero-get-quote">
              Get a Quote
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══ 2. COMPANY INTRO ═══ */}
      <Section className="py-28 lg:py-40 bg-[#0A0A0A] border-t border-white/[0.04]" id="products">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05] max-w-4xl" data-testid="products-heading">
            Most factories still run on<br className="hidden sm:block" />
            <span style={{ color: RED }}>disconnected systems.</span>
          </h2>
          <p className="mt-8 text-lg sm:text-xl text-white/45 max-w-3xl leading-[1.8] font-medium">
            <span className="text-white font-bold">MUSHIN</span> brings plant operations and supplier workflows into one digital backbone. Two products cover every critical function, from the shopfloor to the supply chain.
          </p>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Factory OS */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 lg:p-10 hover:border-[#E8384F]/20 transition-all duration-500" data-testid="product-factory-os">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Internal Operations</p>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-[-0.01em] text-white leading-[1]">Mushin Factory OS</h3>
              <p className="mt-4 text-[15px] text-white/45 leading-[1.7] font-medium">
                The operating system for the modern factory. Modular apps covering quality, production, stores, maintenance, audits, traceability, training, IoT, and more.
              </p>
              <Link to="/products/factory-os" className="mt-8 text-sm font-bold hover:text-white transition-colors inline-flex items-center gap-1.5" style={{ color: RED }} data-testid="product-os-link">
                Explore Factory OS <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Vendor Connect */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 lg:p-10 hover:border-[#38BDF8]/20 transition-all duration-500" data-testid="product-vendor-connect">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BLUE }} />
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: BLUE }}>Supplier Network</p>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-[-0.01em] text-white leading-[1]">Mushin Vendor Connect</h3>
              <p className="mt-4 text-[15px] text-white/45 leading-[1.7] font-medium">
                The supplier quality and vendor governance platform. Connects your factory with its vendors through digital onboarding, audits, scorecards, complaints, and performance tracking.
              </p>
              <Link to="/products/vendor-connect" className="mt-8 text-sm font-bold hover:text-white transition-colors inline-flex items-center gap-1.5" style={{ color: BLUE }} data-testid="product-vc-link">
                Explore Vendor Connect <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 3. WHY MUSHIN — COMPARISONS ═══ */}
      <Section className="py-28 lg:py-40 bg-[#060606] border-t border-white/[0.04]" id="why-mushin">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }} data-testid="why-eyebrow">Why Mushin</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05] max-w-4xl" data-testid="why-heading">
            <span style={{ color: RED }}>Different</span> From Everything<br className="hidden sm:block" />
            You've Tried Before
          </h2>

          {/* Comparison 1: IoT vs Factory OS */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="comparison-iot">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 lg:p-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">What most companies do</p>
              <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white/45 mb-6">IoT Only</h3>
              <div className="space-y-3">
                {iotOnlyItems.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#E02D45]/10 flex items-center justify-center flex-shrink-0">
                      <XIcon className="w-3 h-3 text-[#E02D45]" strokeWidth={2.5} />
                    </div>
                    <span className="text-[15px] text-white/45 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-white/[0.06]">
                <p className="text-sm text-white/35 font-semibold">Incomplete. Only one layer of the factory.</p>
              </div>
            </div>
            <div className="rounded-2xl p-8 lg:p-10" style={{ backgroundColor: `${RED}08`, border: `1px solid ${RED}20` }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: RED }}>What Mushin delivers</p>
              <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mb-6">Full Factory OS</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                {factoryOsItems.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm text-white/65 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${RED}15` }}>
                <p className="text-sm text-white/45 font-semibold">One platform. Every factory function. No blind spots.</p>
              </div>
            </div>
          </div>

          {/* Comparison 2: ERP vs Factory OS */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="comparison-erp">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 lg:p-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">What ERP covers</p>
              <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white/45 mb-6">ERP Layer</h3>
              <div className="space-y-3">
                {erpItems.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#E02D45]/10 flex items-center justify-center flex-shrink-0">
                      <XIcon className="w-3 h-3 text-[#E02D45]" strokeWidth={2.5} />
                    </div>
                    <span className="text-[15px] text-white/45 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-white/[0.06]">
                <p className="text-sm text-white/35 font-semibold">ERP records the business. It does not run the plant.</p>
              </div>
            </div>
            <div className="rounded-2xl p-8 lg:p-10" style={{ backgroundColor: `${RED}08`, border: `1px solid ${RED}20` }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: RED }}>What Mushin runs</p>
              <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mb-6">Factory Execution</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                {mushinOpsItems.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm text-white/65 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${RED}15` }}>
                <p className="text-sm text-white/45 font-semibold">Mushin runs the plant. Where execution actually happens.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 4. FACTORY APPS GRID ═══ */}
      <Section className="py-28 lg:py-40 bg-[#0A0A0A] border-t border-white/[0.04]" id="factory-apps">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }} data-testid="apps-eyebrow">Mushin Factory OS</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05] max-w-4xl" data-testid="apps-heading">
            An <span style={{ color: RED }}>App</span> for Every Core<br className="hidden sm:block" />
            Factory Function
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/40 max-w-2xl leading-[1.7] font-medium">
            Modular apps for every layer of plant operations. Connected, configurable, and running on a single digital backbone.
          </p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {factoryApps.map((app) => (
              <AppCard key={app.slug} name={app.name} desc={app.desc} icon={app.icon} variant="factory" slug={app.slug} />
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ 5. VENDOR CONNECT APPS ═══ */}
      <Section className="py-28 lg:py-40 bg-[#060606] border-t border-white/[0.04]" id="vendor-apps">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: BLUE }} data-testid="vendor-apps-eyebrow">Mushin Vendor Connect</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05] max-w-4xl" data-testid="vendor-apps-heading">
            The Extended<br className="hidden sm:block" />
            <span style={{ color: BLUE }}>Factory Network</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/40 max-w-2xl leading-[1.7] font-medium">
            Connect your factory with its vendors and ancillary suppliers. Onboarding, audits, scorecards, complaints, and performance tracking in one place.
          </p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {vendorApps.map((app) => (
              <AppCard key={app.slug} name={app.name} desc={app.desc} icon={app.icon} variant="vendor" slug={app.slug} />
            ))}
          </div>

          <div className="mt-10">
            <Link to="/products/vendor-connect" className="text-sm font-bold hover:text-white transition-colors inline-flex items-center gap-1.5" style={{ color: BLUE }} data-testid="vendor-explore-link">
              Explore Vendor Connect <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* ═══ 6. INDUSTRIES ═══ */}
      <Section className="py-28 lg:py-40 bg-[#0A0A0A] border-t border-white/[0.04]" id="industries">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }} data-testid="industries-eyebrow">Industries</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05] max-w-4xl" data-testid="industries-heading">
            Same Factory OS.<br className="hidden sm:block" />
            <span style={{ color: RED }}>Tailored by Industry.</span>
          </h2>
          <p className="mt-6 text-lg text-white/40 max-w-2xl leading-[1.7] font-medium">
            The platform stays the same. The app combinations adapt to industry-specific standards and compliance requirements.
          </p>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 lg:gap-6">
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 scrollbar-hide">
              {industries.map((ind, i) => (
                <button key={ind.name} onClick={() => setActiveIndustry(i)}
                  className={`relative text-left px-5 py-4 rounded-xl transition-all duration-300 whitespace-nowrap flex-shrink-0 border ${i === activeIndustry
                    ? 'bg-white/[0.06] border-[#E8384F]/20 text-white'
                    : 'bg-transparent border-white/[0.04] text-white/35 hover:text-white/55 hover:bg-white/[0.03]'
                    }`}
                  data-testid={`industry-tab-${i}`}>
                  <span className={`text-[11px] font-mono block mb-0.5`} style={{ color: i === activeIndustry ? RED : 'rgba(255,255,255,0.15)' }}>0{i + 1}</span>
                  <p className="font-bold text-sm uppercase tracking-[0.04em]">{ind.name}</p>
                  {i === activeIndustry && (
                    <div key={`progress-${activeIndustry}`} className="absolute bottom-0 left-0 h-[2px] rounded-b-xl" style={{ backgroundColor: RED, animation: 'tabProgress 5s linear forwards' }} />
                  )}
                </button>
              ))}
            </div>

            <div className="relative min-h-[360px] sm:min-h-[400px] lg:min-h-[440px] rounded-2xl overflow-hidden border border-white/[0.06]">
              {industries.map((ind, i) => (
                <div key={ind.name} className={`absolute inset-0 transition-all duration-700 ${i === activeIndustry ? 'opacity-100 z-10 scale-100' : 'opacity-0 z-0 scale-[1.02]'}`} data-testid={`industry-panel-${i}`}>
                  <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="relative z-10 p-8 sm:p-10 lg:p-14 flex flex-col justify-end h-full">
                    <div className="flex flex-wrap gap-2.5 mb-4">
                      {ind.standards.map((s) => (
                        <span key={s} className="px-4 py-2 border border-white/20 rounded-lg text-sm font-bold text-white/90 backdrop-blur-sm bg-white/[0.06]">{s}</span>
                      ))}
                    </div>
                    <h3 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-[-0.02em] text-white leading-[0.9]">{ind.name}</h3>
                    <p className="mt-4 text-base sm:text-lg text-white/50 max-w-lg leading-relaxed font-medium">{ind.fit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 7. 100% END-TO-END TRACEABILITY ═══ */}
      <Section className="py-32 lg:py-48 bg-[#060606] border-t border-white/[0.04] relative overflow-hidden" id="traceability" data-testid="traceability-section">
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `linear-gradient(${RED}40 1px, transparent 1px), linear-gradient(90deg, ${RED}40 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm font-bold uppercase tracking-[0.3em] mb-8" style={{ color: RED }} data-testid="trace-eyebrow">The Mushin Promise</p>
            <h2 className="text-4xl sm:text-6xl lg:text-[5.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.9]" data-testid="trace-heading">
              100% <span style={{ color: RED }}>End-to-End</span><br />Traceability
            </h2>
            <p className="mt-8 text-lg text-white/40 max-w-2xl mx-auto leading-[1.8] font-medium">
              From raw material to finished good. Every touch point, every machine event, every quality check. One unbroken digital thread across your entire factory.
            </p>
          </div>

          {/* Trace Flow — Desktop */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-[52px] left-[60px] right-[60px] h-[2px] bg-white/[0.06]" />
              <div className="absolute top-[52px] left-[60px] h-[2px] transition-all duration-700 ease-out" style={{ backgroundColor: RED, width: `${(activeTrace / (traceSteps.length - 1)) * (100 - 8)}%` }} />
              <div className="grid grid-cols-7 gap-4">
                {traceSteps.map((step, i) => {
                  const StepIcon = step.icon;
                  const isActive = i === activeTrace;
                  const isPast = i < activeTrace;
                  return (
                    <div key={step.label} className="flex flex-col items-center text-center" data-testid={`trace-step-${i}`}>
                      <div className={`relative z-10 w-[104px] h-[104px] rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 border ${isActive ? 'bg-[#E02D45]/10 border-[#E02D45]/40 scale-105 shadow-lg shadow-[#E02D45]/10' : isPast ? 'bg-[#E02D45]/[0.04] border-[#E02D45]/15' : 'bg-white/[0.03] border-white/[0.08]'}`}>
                        <StepIcon className={`w-8 h-8 transition-colors duration-500 ${isActive ? 'text-[#E02D45]' : isPast ? 'text-[#E02D45]/60' : 'text-white/30'}`} strokeWidth={1.4} />
                      </div>
                      <h4 className={`text-[13px] font-bold leading-tight transition-colors duration-500 ${isActive ? 'text-white' : isPast ? 'text-white/60' : 'text-white/40'}`}>{step.label}</h4>
                      <p className={`text-[11px] mt-1 font-medium transition-colors duration-500 ${isActive ? 'text-white/50' : 'text-white/20'}`}>{step.sub}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Trace Flow — Mobile */}
          <div className="lg:hidden space-y-3">
            {traceSteps.map((step, i) => {
              const StepIcon = step.icon;
              return (
                <div key={step.label} className="flex items-center gap-4" data-testid={`trace-step-mobile-${i}`}>
                  <div className="relative flex flex-col items-center">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                      <StepIcon className="w-5 h-5" style={{ color: `${RED}AA` }} strokeWidth={1.5} />
                    </div>
                    {i < traceSteps.length - 1 && (
                      <div className="w-[2px] h-3 mt-1" style={{ backgroundColor: `${RED}25` }} />
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white/70">{step.label}</h4>
                    <p className="text-xs text-white/25 font-medium">{step.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-sm text-white/20 font-bold uppercase tracking-[0.2em]">
              Zero gaps. Zero paper. Zero guesswork.
            </p>
          </div>
        </div>
      </Section>

      {/* ═══ 8. INDIA MAP ═══ */}
      <Section className="py-28 lg:py-40 bg-[#0A0A0A] border-t border-white/[0.04]" id="india-map">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }}>Deployments</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                Operating Across<br /><span style={{ color: RED }}>India's Industrial Belt</span>
              </h2>
              <p className="mt-6 text-base text-white/40 max-w-md leading-[1.7] font-medium">
                Active deployments across diverse manufacturing clusters and industries.
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {mapCities.map((city) => (
                  <div key={city.name} className="flex items-center gap-2.5">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: RED }} strokeWidth={2.5} />
                    <span className="text-sm text-white/55 font-semibold">{city.name}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300 inline-flex items-center gap-2.5 hover:shadow-xl" style={{ backgroundColor: RED }} onMouseEnter={e => e.currentTarget.style.backgroundColor = RED_HOVER} onMouseLeave={e => e.currentTarget.style.backgroundColor = RED} data-testid="map-demo-cta">
                  Book a Demo <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            <div className="flex justify-center" data-testid="india-cluster-map">
              <IndiaMapReal />
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 9. CLIENTS ═══ */}
      <section className="py-16 bg-[#060606] border-t border-white/[0.04] overflow-hidden" data-testid="trust-strip">
        <p className="text-center text-sm font-bold uppercase tracking-[0.2em] text-white/20 mb-10">
          Trusted Across India's Manufacturing Ecosystem
        </p>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#060606] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#060606] to-transparent z-10" />
          <div className="flex items-center gap-6 trust-scroll">
            {[...Array(2)].flatMap((_, rep) => clientsRow1.map((c) => (
              <div key={`${rep}-${c.name}`} className="flex-shrink-0 h-16 px-6 rounded-lg bg-white border border-white/10 flex items-center justify-center shadow-sm" data-testid={`trust-logo-${c.name}`}>
                <img src={c.logo} alt={c.name} className="h-12 max-w-[160px] object-contain" loading="lazy" />
              </div>
            )))}
          </div>
          <div className="flex items-center gap-6 trust-scroll-reverse mt-4">
            {[...Array(2)].flatMap((_, rep) => clientsRow2.map((c) => (
              <div key={`r${rep}-${c.name}`} className="flex-shrink-0 h-16 px-6 rounded-lg bg-white border border-white/10 flex items-center justify-center shadow-sm">
                <img src={c.logo} alt={c.name} className="h-12 max-w-[160px] object-contain" loading="lazy" />
              </div>
            )))}
          </div>
        </div>
      </section>

      {/* ═══ 10. FINAL CTA ═══ */}
      <section className="py-32 lg:py-48 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="final-cta">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[150px]" style={{ backgroundColor: `${RED}08` }} />
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-10" style={{ color: RED }}>Get Started</p>
          <h2 className="text-3xl sm:text-5xl lg:text-[4rem] font-black uppercase tracking-[-0.02em] text-white leading-[1]" data-testid="cta-heading">
            Run Your Factory<br />on <span className="cta-heartbeat" style={{ color: RED }}>Mushin</span>
          </h2>
          <p className="mt-8 text-white/35 text-lg max-w-xl mx-auto font-medium leading-[1.8]">
            One digital operating backbone for the entire factory. From the shopfloor to the supply chain.
          </p>
          <div className="mt-12 flex flex-wrap gap-5 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300 inline-flex items-center gap-2.5 hover:shadow-xl" style={{ backgroundColor: RED }} onMouseEnter={e => e.currentTarget.style.backgroundColor = RED_HOVER} onMouseLeave={e => e.currentTarget.style.backgroundColor = RED} data-testid="final-book-demo">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/40 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300" data-testid="final-get-quote">
              Get a Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
